import { sortUsages } from '@miq/domain';
import { transformUsageToRef } from '@core/composers/usages/usage';
import { multipleSimpleValueValidation, simpleValueValidation } from '@shared/utils/helpers/records.helpers';
import { withData, WithDataDef, withIdRetrieval, WithIdRetrievalDef, withUoms, WithUomsDef } from './base-composer';
import { BaseEdp } from './entity';
import { withPrimaryImage, withPropertyMedia } from './media/media';
import { withPostalAddress, WithPostalAddressDef } from './postal-address/postal-address';
import { PropertyUsage, PropertyUsageDef } from './usages/usage';
import { withUsages, WithUsagesDef } from './usages/usages';

/**
 * Map with property ref structure used to extract data from a property record
 * using a miner
 *
 * @see miner
 */
const PROPERTY_TO_REF_MAPPING = {
  id: 'entity.id',
  property_name: 'property_name',
  property_descr: 'property_descr',
  ref_property_tenancy_type_desc: 'ref_property_tenancy_type_desc',
  ref_property_construction_type_desc: 'ref_property_construction_type_desc',
  net_rentable_area: 'net_rentable_area',
  total_gross_area: 'total_gross_area',
  total_gross_area_uom_desc: 'total_gross_area_uom_desc',
  total_number_of_floors: 'total_number_of_floors',
  year_property_built: 'year_property_built',
  number_parking_space: 'number_parking_space',
  parking_ratio: 'parking_ratio',
  total_land_area: 'total_land_area',
  total_land_area_uom_desc: 'total_land_area_uom_desc',
  postal_address: 'postal_address',
  ref_built_to_specification_desc: 'ref_built_to_specification_desc',
  floor_loading_capacity: 'floor_loading_capacity',
  ref_property_status_desc: 'ref_property_status_desc',
  property_external_ratings: 'property_external_ratings',
  property_measurement: 'property_measurement',
  power_contracted_for_building: 'power_contracted_for_building',
  power_contracted_for_building_uom: 'power_contracted_for_building_uom',
  power_future_potential_for_building: 'power_future_potential_for_building',
  power_future_potential_for_building_uom: 'power_future_potential_for_building_uom',
  power_maximum_accommodated_for_building: 'power_maximum_accommodated_for_building',
  power_maximum_accommodated_for_building_uom: 'power_maximum_accommodated_for_building_uom',
};

export interface PropertyDef
  extends BaseEdp, WithIdRetrievalDef, WithUomsDef, WithPostalAddressDef, WithDataDef, WithUsagesDef {
  [key: string]: any;
}

/**
 * Full Property composer function
 * @see https://medium.com/humans-create-software/composition-over-inheritance-cb6f88070205
 */
export const Property = (state): PropertyDef => {
  sortUsages(state);
  return Object.assign(
    {},
    state,
    // composers
    withIdRetrieval(),
    withData(),
    withUoms(),
    withPostalAddress(),
    withUsages(state),
    withPropertyMedia(),
    withPrimaryImage()
  );
};

/**
 * Data class definition for PropertyRef composer
 */
export interface PropertyRefDef extends WithUomsDef, WithPostalAddressDef, WithDataDef, PropertyUsageDef {
  property_usage: PropertyUsageDef;
  [key: string]: any;
}

/**
 * Property subset composer used for availability, sale & lease
 */
export const PropertyRef = (state): PropertyRefDef => {
  return Object.assign(
    {},
    state,
    { property_usage: PropertyUsage(state.property_usage) },
    // composers
    withIdRetrieval(),
    withData(),
    withUoms(),
    withPostalAddress()
  );
};

export enum PropertyExtractionMode {
  primary = 'primary',
  primaryOrFirst = 'primary-or-first',
  id = 'id',
  custom = 'custom',
  noUsage = 'no-usage',
}

/**
 * Transforms a Property composed object into a PropertyRef composed object
 * extracting a specific usage due to the ref requiring only one.
 *
 * @param property            Property composed object
 * @param usageExtractionMode Key used to know the way of extracting a usage
 *                              - primary: will extract primary usage
 *                              - primary-or-first: will extract primary usage or the first usage of the array
 *                              - id: will search and extract an usage by id
 *                              - custom: will search and extract an usage by a specified path and value
 * @param values       Optional values required for ID or Custom mode
 * @param values.id    Optional id value to use when extraction mode is `id`
 * @param values.path  Optional path value to use when extraction mode is `custom`
 * @param values.value Optional value to use when extraction mode is `custom`
 */
export function transformPropertyToRef(
  property,
  usageExtractionMode: PropertyExtractionMode,
  values?: { id?: number; path?: string; value?: string | number }
) {
  let getUsage;
  let args = [];
  if (usageExtractionMode.includes('primary')) {
    getUsage = usageExtractionMode === PropertyExtractionMode.primary ? 'primaryUsage' : 'primaryUsageOrFirstOrEmpty';
  } else if (usageExtractionMode === PropertyExtractionMode.id && !!values && simpleValueValidation(values.id)) {
    getUsage = 'getUsageById';
    args = [values.id];
  } else if (
    usageExtractionMode === PropertyExtractionMode.custom &&
    !!values &&
    multipleSimpleValueValidation(values.path, values.value)
  ) {
    getUsage = 'getUsageBy';
    args = [values.path, values.value];
  } else if (usageExtractionMode === PropertyExtractionMode.noUsage) {
    getUsage = args = null;
  } else {
    throw new Error('Incorrect or missing values specified for required usage extraction mode');
  }
  const extractedUsage = getUsage && args ? property[getUsage](...args) : null;
  return PropertyRef({
    ...property.getData(PROPERTY_TO_REF_MAPPING),
    property_usage: extractedUsage ? transformUsageToRef(extractedUsage) : {},
  });
}
